import { useForm } from 'react-hook-form';
import { Modal, Input, Select, Button } from '@/components/ui';
import { useCreateEmployee, useUpdateEmployee } from '@/hooks/useEmployees';
import type { CreateEmployeeInput, Employee } from '@aelita/shared';

export interface EmployeeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee | null;
}

export function EmployeeFormModal({ isOpen, onClose, employee }: EmployeeFormModalProps) {
  const createEmployee = useCreateEmployee();
  const updateEmployee = useUpdateEmployee();
  const isEditing = !!employee;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateEmployeeInput>({
    defaultValues: employee
      ? {
          firstName: employee.firstName,
          lastName: employee.lastName,
          email: employee.email,
          phone: employee.phone ?? '',
          role: employee.role,
          title: employee.title ?? '',
          department: employee.department ?? '',
          hourlyRate: employee.hourlyRate ?? undefined,
          billableRate: employee.billableRate ?? undefined,
          status: employee.status,
        }
      : {},
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CreateEmployeeInput) => {
    try {
      if (isEditing) {
        // Don't send password if editing and it's empty
        const { password, ...rest } = data;
        const updateData = password ? { ...rest, password } : rest;
        await updateEmployee.mutateAsync({
          id: employee.id,
          data: updateData,
        });
      } else {
        await createEmployee.mutateAsync(data);
      }
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to save employee:', error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Edit Employee' : 'Add New Employee'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            {...register('firstName', { required: 'First name is required' })}
            error={errors.firstName?.message}
            defaultValue={employee?.firstName}
          />
          <Input
            label="Last Name"
            {...register('lastName', { required: 'Last name is required' })}
            error={errors.lastName?.message}
            defaultValue={employee?.lastName}
          />
        </div>

        <Input
          label="Email"
          type="email"
          {...register('email', { required: 'Email is required' })}
          error={errors.email?.message}
          defaultValue={employee?.email}
          disabled={isEditing}
        />

        <Input
          label={isEditing ? 'New Password (leave blank to keep current)' : 'Password'}
          type="password"
          {...register('password', {
            required: isEditing ? false : 'Password is required',
            minLength: { value: 8, message: 'Password must be at least 8 characters' },
          })}
          error={errors.password?.message}
        />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Role"
            {...register('role')}
            options={[
              { value: 'developer', label: 'Developer' },
              { value: 'designer', label: 'Designer' },
              { value: 'qa', label: 'QA Engineer' },
              { value: 'devops', label: 'DevOps Engineer' },
              { value: 'pm', label: 'Project Manager' },
              { value: 'manager', label: 'Manager' },
              { value: 'admin', label: 'Administrator' },
            ]}
            defaultValue={employee?.role ?? 'developer'}
          />
          <Select
            label="Status"
            {...register('status')}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'on_leave', label: 'On Leave' },
            ]}
            defaultValue={employee?.status ?? 'active'}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Title"
            {...register('title')}
            placeholder="e.g., Senior Developer"
            defaultValue={employee?.title ?? ''}
          />
          <Input
            label="Department"
            {...register('department')}
            defaultValue={employee?.department ?? ''}
          />
        </div>

        <Input label="Phone" {...register('phone')} defaultValue={employee?.phone ?? ''} />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Hourly Rate"
            type="number"
            {...register('hourlyRate', { valueAsNumber: true })}
            placeholder="Internal rate"
            defaultValue={employee?.hourlyRate ?? ''}
          />
          <Input
            label="Billable Rate"
            type="number"
            {...register('billableRate', { valueAsNumber: true })}
            placeholder="Client rate"
            defaultValue={employee?.billableRate ?? ''}
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={createEmployee.isPending || updateEmployee.isPending}>
            {isEditing ? 'Save Changes' : 'Add Employee'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
